import { wsLifecycleEmitter } from "@qw/graphql";
import { useEffect } from "react";
import { useEventCallback } from "usehooks-ts";

import { useVisibilityRisingEdge } from "./use-visibility-rising-edge";

/**
 * One freshness-detection seam for the server-authoritative chat render (brief 014 Phase 5).
 *
 * Owns the two AMBIENT "conversation may be stale" sources and nothing else:
 *   - **tab-return** (hidden→visible rising edge), and
 *   - **graphql-ws reconnect** (a `connected` after a `closed`) — graphql-ws does not replay events
 *     published while the socket was down, so a dropped subscription needs an out-of-band re-probe.
 *
 * It fans them out to two injected leaves, which keep their own (divergent) dispatch gating:
 *   - `refetchHistory()` — refetch the persisted `conversationMessages` tree. Idempotent/coalesced,
 *     safe anytime; only meaningful on the server-history path, so gated by `enabled`.
 *   - `ensureAttached()` — re-attach the live SSE stream. Self-gates (suppresses during an in-flight
 *     turn / elicit submit) and defers via the caller's own probe latch, so it is called
 *     unconditionally here in both render modes.
 *
 * This replaces the two independent `useVisibilityRisingEdge` listeners (previously one each in
 * `useConversationMessages` and `useMcpChat`) and the WS-reconnect effect in `useMcpChat`. The topic
 * subscriptions (`conversationHistoryChanged`, `chatSessionStreamStateChanged`) stay in their owning
 * hooks — they carry distinct payloads/semantics and each already drives exactly one leaf — so this
 * controller owns only the two ambient edges that were genuinely duplicated across the stacks.
 */

interface UseConversationFreshnessArgs {
  /** Gate for the `refetchHistory` leaf — the server-authoritative render flag. */
  enabled: boolean;
  refetchHistory: () => void;
  ensureAttached: () => void;
}

export const useConversationFreshness = ({
  enabled,
  refetchHistory,
  ensureAttached,
}: UseConversationFreshnessArgs): void => {
  // One stable fan-out that always reads the latest props (`useEventCallback`, the same primitive
  // `useVisibilityRisingEdge` uses). The WS listener can bind once (stable dep) and never observe a
  // stale `enabled`/leaf closure — e.g. a reconnect landing right after a flag flip still refetches.
  const onStale = useEventCallback(() => {
    if (enabled) {
      refetchHistory();
    }
    ensureAttached();
  });

  // Tab-return: refetch persisted history (server-history path) + re-attach the live stream. The
  // poll is paused while hidden, so the rising edge is the immediate recovery (B-1341 / B-1345).
  useVisibilityRisingEdge(onStale);

  // graphql-ws reconnect edge. Latch a `closed` first so the initial `connected` (already covered by
  // the mount probe) is skipped; only the reconnect edge re-probes. Running the same `onStale` as the
  // tab-return edge also refetches persisted history, closing the up-to-30s staleness window that
  // existed before (`useConversationMessages` has no WS listener, so after a socket drop it recovered
  // persisted content only on the next 30s poll — the push subscription's redeliveries are lost while
  // disconnected).
  useEffect(() => {
    let sawClosed = false;
    const onClosed = () => {
      sawClosed = true;
    };
    const onConnected = () => {
      if (!sawClosed) {
        return;
      }
      sawClosed = false;
      onStale();
    };
    wsLifecycleEmitter.addEventListener("closed", onClosed);
    wsLifecycleEmitter.addEventListener("connected", onConnected);
    return () => {
      wsLifecycleEmitter.removeEventListener("closed", onClosed);
      wsLifecycleEmitter.removeEventListener("connected", onConnected);
    };
  }, [onStale]);
};
